/**
 * TakomoModal — Standalone modal/drawer for Shopify themes
 *
 * Desktop: centered modal with scale+fade animation
 * Mobile (<768px): bottom drawer with drag-to-dismiss
 *
 * Usage:
 *   TakomoModal.open({ content: '<h2>Hello</h2>', theme: 'dark' });
 *   TakomoModal.open({ el: document.getElementById('my-content') });
 *   TakomoModal.close();
 *
 * Trigger from HTML:
 *   <button data-tkm-open="my-modal">Open</button>
 *   <template id="my-modal"><p>Modal content here</p></template>
 */
(function () {
  'use strict';

  var MOBILE_BP = 768;
  var SNAP_CLOSE_PERCENT = 0.5;
  var SNAP_CLOSE_VELOCITY = 1.2;
  var TRANSITION = 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.32s ease';

  var state = {
    overlay: null,
    sheet: null,
    backdrop: null,
    onClose: null,
    drag: { startY: 0, currentY: 0, startTime: 0, active: false },
    handlers: {}
  };

  function isMobile() {
    return window.innerWidth < MOBILE_BP;
  }

  function open(options) {
    if (state.overlay) close();

    options = options || {};
    var theme = options.theme || 'dark';
    var maxWidth = options.maxWidth || '752px';
    state.onClose = options.onClose || null;

    // Build DOM
    var overlay = document.createElement('div');
    overlay.className = 'tkm-overlay';
    overlay.setAttribute('data-tkm-theme', theme);

    var backdrop = document.createElement('div');
    backdrop.className = 'tkm-backdrop';
    overlay.appendChild(backdrop);

    var sheet = document.createElement('div');
    sheet.className = 'tkm-sheet';
    sheet.style.maxWidth = maxWidth;

    // Drag handle (visible on mobile via CSS)
    var handle = document.createElement('div');
    handle.className = 'tkm-drag-handle';
    var bar = document.createElement('div');
    bar.className = 'tkm-drag-handle-bar';
    handle.appendChild(bar);
    sheet.appendChild(handle);

    // Close button (visible on desktop via CSS)
    var closeBtn = document.createElement('button');
    closeBtn.className = 'tkm-close';
    closeBtn.setAttribute('type', 'button');
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '\u2715';
    sheet.appendChild(closeBtn);

    // Content
    var content = document.createElement('div');
    content.className = 'tkm-content';

    if (options.el) {
      content.appendChild(options.el.cloneNode(true));
    } else if (options.content) {
      content.innerHTML = options.content;
    }

    sheet.appendChild(content);

    if (options.closeButton) {
      var footer = document.createElement('div');
      footer.className = 'tkm-footer';
      var footerBtn = document.createElement('button');
      footerBtn.className = 'tkm-footer-close';
      footerBtn.setAttribute('type', 'button');
      footerBtn.textContent = options.closeLabel || 'Close';
      footerBtn.addEventListener('click', close);
      footer.appendChild(footerBtn);
      sheet.appendChild(footer);
    }

    overlay.appendChild(sheet);
    document.body.appendChild(overlay);

    state.overlay = overlay;
    state.sheet = sheet;
    state.backdrop = backdrop;

    // Lock scroll
    document.body.style.overflow = 'hidden';

    // Bind events
    backdrop.addEventListener('click', close);
    closeBtn.addEventListener('click', close);

    state.handlers.esc = function (e) {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', state.handlers.esc);

    // Drag-to-dismiss (mobile)
    handle.addEventListener('pointerdown', onDragStart);

    // Animate in
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.classList.add('tkm-overlay--open');
      });
    });
  }

  function close() {
    if (!state.overlay) return;

    var overlay = state.overlay;
    var callback = state.onClose;

    // Animate out
    overlay.classList.remove('tkm-overlay--open');

    setTimeout(function () {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      document.body.style.overflow = '';
    }, 320);

    // Cleanup
    if (state.handlers.esc) {
      document.removeEventListener('keydown', state.handlers.esc);
    }

    state.overlay = null;
    state.sheet = null;
    state.backdrop = null;
    state.onClose = null;
    state.handlers = {};

    if (callback) callback();
  }

  function setContent(html) {
    if (!state.overlay) return;
    var content = state.overlay.querySelector('.tkm-content');
    if (content) content.innerHTML = html;
  }

  function isOpen() {
    return !!state.overlay;
  }

  // --- Drag-to-dismiss ---

  function onDragStart(e) {
    var y = e.clientY;
    state.drag = { startY: y, currentY: y, startTime: Date.now(), active: true };
    state.sheet.style.transition = 'none';

    state.handlers.move = function (ev) { onDragMove(ev); };
    state.handlers.end = function () { onDragEnd(); };
    window.addEventListener('pointermove', state.handlers.move);
    window.addEventListener('pointerup', state.handlers.end);
  }

  function onDragMove(e) {
    if (!state.drag.active) return;
    var delta = Math.max(0, e.clientY - state.drag.startY);
    state.drag.currentY = e.clientY;
    state.sheet.style.transform = 'translateY(' + delta + 'px)';
  }

  function onDragEnd() {
    window.removeEventListener('pointermove', state.handlers.move);
    window.removeEventListener('pointerup', state.handlers.end);
    if (!state.drag.active) return;
    state.drag.active = false;

    var delta = Math.max(0, state.drag.currentY - state.drag.startY);
    var sheetHeight = state.sheet.offsetHeight;
    var elapsed = Date.now() - state.drag.startTime;
    var velocity = elapsed > 0 ? delta / elapsed : 0;

    // Re-enable transition for the animation
    state.sheet.style.transition = TRANSITION;

    if (delta > sheetHeight * SNAP_CLOSE_PERCENT || velocity > SNAP_CLOSE_VELOCITY) {
      // Slide to bottom, then clean up
      var overlay = state.overlay;
      var callback = state.onClose;

      state.sheet.style.transform = 'translateY(100%)';
      state.backdrop.style.opacity = '0';

      setTimeout(function () {
        if (overlay && overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
        document.body.style.overflow = '';
        if (state.handlers.esc) {
          document.removeEventListener('keydown', state.handlers.esc);
        }
        state.overlay = null;
        state.sheet = null;
        state.backdrop = null;
        state.onClose = null;
        state.handlers = {};
        if (callback) callback();
      }, 320);
    } else {
      // Snap back to open position
      state.sheet.style.transform = 'translateY(0)';
    }
  }

  // --- Declarative triggers ---
  // <button data-tkm-open="my-modal">Open</button>
  // <template id="my-modal"><p>Content</p></template>

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-tkm-open]');
    if (!trigger) return;

    e.preventDefault();
    var targetId = trigger.getAttribute('data-tkm-open');
    var source = document.getElementById(targetId);

    if (!source) return;

    var html = source.tagName === 'TEMPLATE'
      ? source.innerHTML
      : source.innerHTML;

    open({
      content: html,
      theme: trigger.getAttribute('data-tkm-theme') || 'dark'
    });
  });

  // --- Public API ---
  window.TakomoModal = {
    open: open,
    close: close,
    setContent: setContent,
    isOpen: isOpen
  };
})();